import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { formsAPI, clientsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Download, 
  CheckCircle, 
  FileText,
  Home,
  Shield,
  ClipboardList,
  Users,
  ArrowRightLeft,
  Eye,
  Loader2
} from 'lucide-react';

const AVIDA_LOGO = "https://customer-assets.emergentagent.com/job_packet-digitizer/artifacts/f0t7kezs_Avida%20Logo%20Full%20Color.png";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const formInfo = {
  medical_release: { 
    name: 'Medical Release', 
    icon: Shield,
    recipient: 'Physician/Facility'
  },
  screening_sheet: { 
    name: 'Screening Sheet', 
    icon: ClipboardList,
    recipient: 'Internal Records'
  },
  screening_form: { 
    name: 'Screening Form', 
    icon: ClipboardList,
    recipient: 'Internal Records'
  },
  specialist_form: { 
    name: 'Specialist Form', 
    icon: Users,
    recipient: 'Specialists'
  },
  pcst_authorization: { 
    name: 'PCST Authorization', 
    icon: FileText,
    recipient: 'PCST/Prior Auth'
  },
  client_transfer: { 
    name: 'Client Transfer Request', 
    icon: ArrowRightLeft,
    recipient: 'Previous Provider'
  },
  acknowledgment: { 
    name: 'Acknowledgment Signature', 
    icon: CheckCircle,
    recipient: 'Client File'
  },
};

export default function PacketCompletePage() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const location = useLocation();
  const [submissions, setSubmissions] = useState(location.state?.submissions || []);
  const [client, setClient] = useState(location.state?.client || null);
  const [isLoading, setIsLoading] = useState(!location.state?.submissions);
  const [downloadingId, setDownloadingId] = useState(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  useEffect(() => {
    if (!location.state?.submissions && clientId) {
      loadCompletedForms();
    }
  }, [clientId]);

  const loadCompletedForms = async () => {
    setIsLoading(true);
    try {
      const [clientData, formsData] = await Promise.all([
        clientsAPI.getById(clientId),
        formsAPI.getCompleted(clientId)
      ]);
      setClient(clientData);
      setSubmissions(formsData);
    } catch (error) {
      toast.error('Failed to load completed forms');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadAll = async () => {
    if (submissions.length === 0) return;
    
    setIsDownloadingAll(true);
    toast.info(`Starting download of ${submissions.length} PDFs...`);
    
    for (let i = 0; i < submissions.length; i++) {
      await downloadSingle(submissions[i], i);
      // Small delay between downloads to prevent browser throttling
      if (i < submissions.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    setIsDownloadingAll(false);
    toast.success(`All ${submissions.length} PDFs downloaded!`);
  };

  const downloadSingle = async (sub, index) => {
    const info = formInfo[sub.form_type] || { name: `Form ${index + 1}` };
    const token = localStorage.getItem('token');
    const url = `${API_BASE}/api/forms/completed/${sub.id}/pdf/download?token=${token}`;
    const fileName = `${info.name.replace(/\s+/g, '_')}-${client?.last_name || 'Client'}.pdf`;
    
    setDownloadingId(sub.id);
    
    try {
      // Fetch the PDF as a blob
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Check if blob has content
      if (blob.size === 0) {
        throw new Error('Empty PDF received');
      }
      
      // Create blob URL and download
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = blobUrl;
      link.download = fileName;
      link.setAttribute('download', fileName);
      
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 150);
      
      toast.success(`Downloaded ${info.name}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download ${info.name}`);
      
      // Fallback: open in new tab for manual download
      window.open(url, '_blank');
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePreview = (sub) => {
    navigate(`/clients/${clientId}/forms/${sub.id}/preview`, {
      state: { client, formType: sub.form_type }
    });
  };

  const handleBackToClients = () => {
    navigate('/clients');
  };

  const handleNewPacket = () => {
    navigate(`/clients/${clientId}/packet`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-800 to-blue-900 flex items-center justify-center">
        <div className="text-center">
          <img src={AVIDA_LOGO} alt="Avida" className="h-16 mx-auto mb-4 animate-pulse" />
          <Loader2 className="w-8 h-8 animate-spin text-amber-400 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-800 to-blue-900 flex flex-col">
      {/* Success Header */}
      <div className="py-8 px-6 text-center">
        <img 
          src={AVIDA_LOGO} 
          alt="Avida" 
          className="h-16 mx-auto mb-4"
        />
        
        <div className="w-16 h-16 rounded-full bg-amber-400 flex items-center justify-center mb-4 mx-auto">
          <CheckCircle className="w-8 h-8 text-blue-900" />
        </div>
        
        <h1 
          className="text-2xl font-bold text-white mb-1"
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {submissions.length > 0 ? 'Packet Complete!' : 'Completed Forms'}
        </h1>
        
        <p className="text-amber-200">
          {client?.first_name} {client?.last_name}
        </p>
      </div>

      {/* Forms List */}
      <div className="flex-1 bg-white rounded-t-3xl p-6 space-y-4 safe-area-bottom overflow-auto">
        {submissions.length > 0 ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {submissions.length} Separate PDFs Generated
              </p>
              <Button
                onClick={handleDownloadAll}
                size="sm"
                disabled={isDownloadingAll}
                className="bg-amber-400 hover:bg-amber-500 text-blue-900 font-semibold disabled:opacity-50"
                data-testid="download-all-button"
              >
                {isDownloadingAll ? (
                  <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Downloading...</>
                ) : (
                  <><Download className="mr-1 h-4 w-4" /> Download All</>
                )}
              </Button>
            </div>

            <div className="space-y-2">
              {submissions.map((sub, index) => {
                const info = formInfo[sub.form_type] || { 
                  name: `Form ${index + 1}`, 
                  icon: FileText,
                  recipient: 'Unknown'
                };
                const isDownloading = downloadingId === sub.id;
                const IconComponent = info.icon;
                
                return (
                  <Card 
                    key={sub.id}
                    className="p-4 border-amber-100 hover:bg-amber-50 transition-all"
                    data-testid={`pdf-card-${sub.form_type}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-blue-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {info.name}
                        </p>
                        <p className="text-sm text-blue-600">
                          Send to: {info.recipient}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-blue-200 text-blue-600 hover:bg-blue-50"
                          onClick={() => handlePreview(sub)}
                          data-testid={`preview-${sub.form_type}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Preview
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-blue-600 hover:text-blue-800 hover:bg-amber-100 disabled:opacity-50"
                          onClick={() => downloadSingle(sub, index)}
                          disabled={isDownloading}
                          data-testid={`download-${sub.form_type}`}
                        >
                          {isDownloading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Download className="w-5 h-5" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <FileText className="w-16 h-16 text-blue-200 mx-auto mb-4" />
            <p className="text-blue-600">No completed forms found for this client.</p>
          </div>
        )}

        <div className="pt-4 border-t border-amber-100 space-y-3">
          <Button
            onClick={handleNewPacket}
            className="w-full h-12 btn-avida"
            data-testid="new-packet-button"
          >
            <FileText className="mr-2 h-5 w-5" />
            Start New Packet
          </Button>
          <Button
            onClick={handleBackToClients}
            variant="outline"
            className="w-full h-12 border-amber-300 text-blue-800 hover:bg-amber-50"
            data-testid="back-to-clients-button"
          >
            <Home className="mr-2 h-5 w-5" />
            Back to Client Search
          </Button>
        </div>
      </div>
    </div>
  );
}
